'use client';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { faPhone } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { generateWhatsappLink } from '@/utils/formatters/format-whatsapp';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { Partner } from '@/types';

interface FlexSupportProps {
  professionalName: string;
  partner: Partner;
  guideCode: string;
  documentNumber: string;
}

export function FlexSupport({ professionalName, partner, guideCode, documentNumber }: FlexSupportProps) {
  const whatsapp = partner.facility?.contacts?.whatsapp;
  const phone = partner.facility?.contacts?.phones?.[0];

  const whatsappLink = useMemo(() => {
    const text = `Olá, comprei um horário no site da Medprev e gostaria de fazer o agendamento do horário. GUIA: ${guideCode}. CPF: ${documentNumber}`;
    return generateWhatsappLink(whatsapp, text);
  }, [documentNumber, guideCode, whatsapp]);

  return (
    <div
      data-testid={DataTestIdEnum.APPOINTMENT_CARD_FLEX_SUPPORT}
      className="flex flex-col gap-3 border border-warning-400 rounded-xl p-4"
    >
      <div className="flex items-center gap-2">
        <Icon icon={faCircleExclamation} className="text-warning-400 text-xl flex-shrink-0" />
        <Typography variant={TypographyVariant.BODY_TEXT} className="font-bold text-gray-900">Encaixe</Typography>
      </div>

      <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
        Você comprou um Encaixe. Agora, é preciso entrar em contato com a
        {' '}
        <strong>{professionalName}</strong>
        {' '}
        para combinar a data e o horário da sua consulta, por meio de um dos contatos abaixo.
      </Typography>

      {whatsapp && (
        <Button asChild startIcon={faWhatsapp} variant="secondary">
          <Link prefetch={false} href={whatsappLink} target="_blank" rel="noopener">
            {maskPhone(whatsapp)}
          </Link>
        </Button>
      )}

      {phone && (
        <Button asChild startIcon={faPhone} variant="secondary">
          <Link prefetch={false} href={`tel:${phone}`} target="_blank" rel="noopener">
            {maskPhone(phone)}
          </Link>
        </Button>
      )}
    </div>
  );
}
